import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { capsules } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Clock, CheckCircle2, Send } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import PageHead from "@/components/PageHead";

function timeUntil(iso) {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Ready";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const [list, setList] = useState(null);

  const load = async () => {
    try {
      setList(await capsules.list());
    } catch (e) {
      toast.error("Failed to load capsules");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const stats = {
    total: list?.length || 0,
    scheduled: list?.filter((c) => c.status === "scheduled").length || 0,
    released: list?.filter((c) => c.status === "released").length || 0,
  };

  return (
    <>
      <PageHead title="Your Vault | ChronoVaults" noindex />
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <p className="label-mono text-terracotta">Your Vault</p>
          <h1 className="font-serif text-5xl text-bone mt-3 leading-none">
            Hello, {user?.name?.split(" ")[0] || "friend"}.
          </h1>
          <p className="text-white/50 mt-3 text-sm">
            {stats.total === 0 ? "No capsules sealed yet — begin your first below." : `You have ${stats.scheduled} sealed and ${stats.released} released.`}
          </p>
        </div>
        <Link to="/capsules/new" data-testid="new-capsule-cta">
          <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full h-12 px-7">
            <Plus className="mr-2 w-4 h-4" /> New capsule
          </Button>
        </Link>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-3 gap-px bg-white/5 mb-12" data-testid="stat-strip">
        <div className="bg-vault-950 p-6">
          <p className="label-mono">Total sealed</p>
          <p className="font-serif text-4xl text-bone mt-2">{stats.total}</p>
        </div>
        <div className="bg-vault-950 p-6">
          <p className="label-mono">Awaiting release</p>
          <p className="font-serif text-4xl text-terracotta mt-2">{stats.scheduled}</p>
        </div>
        <div className="bg-vault-950 p-6">
          <p className="label-mono">Delivered</p>
          <p className="font-serif text-4xl text-bone mt-2">{stats.released}</p>
        </div>
      </div>

      {/* Grid */}
      {list === null ? (
        <div className="text-white/40 text-sm">Loading vault...</div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center" data-testid="empty-state">
          <Send className="mx-auto w-8 h-8 text-terracotta mb-6" strokeWidth={1.2} />
          <p className="font-serif text-3xl text-bone">Your vault is empty.</p>
          <p className="text-white/50 mt-3 mb-8 max-w-md mx-auto text-sm">
            Record a video, choose a recipient, set the release moment. We'll do the rest.
          </p>
          <Link to="/capsules/new">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
              Seal your first
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="capsule-grid">
          {list.map((c) => (
            <Link
              key={c.id}
              to={`/capsules/${c.id}`}
              className="vault-card p-6 block"
              data-testid={`capsule-card-${c.id}`}
            >
              <div className="flex items-start justify-between mb-6">
                <span
                  className={`label-mono px-2 py-1 border ${
                    c.status === "released"
                      ? "border-terracotta/40 text-terracotta"
                      : "border-white/15 text-white/60"
                  }`}
                >
                  {c.status === "released" ? "Delivered" : "Sealed"}
                </span>
                {c.status === "released" ? (
                  <CheckCircle2 className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                ) : (
                  <Clock className="w-4 h-4 text-white/40" strokeWidth={1.5} />
                )}
              </div>
              <h3 className="font-serif text-2xl text-bone leading-tight line-clamp-2">{c.title}</h3>
              <p className="text-xs text-white/50 mt-2">To {c.recipient_name}</p>

              <div className="mt-6 pt-6 border-t border-white/5 grid grid-cols-2 gap-3">
                <div>
                  <p className="label-mono text-[9px]">Release</p>
                  <p className="text-xs text-white/70 mt-1">{fmt(c.release_at)}</p>
                </div>
                <div>
                  <p className="label-mono text-[9px]">{c.status === "released" ? "Delivered" : "In"}</p>
                  <p className="text-xs text-terracotta mt-1">
                    {c.status === "released" ? fmt(c.released_at) : timeUntil(c.release_at)}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
